import { Ticket } from './types';
import { readPreference, writePreference } from './storage';

const BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const client = readPreference('triage-client') || crypto.randomUUID();
writePreference('triage-client', client);

export class ApiError extends Error {
  constructor(message: string, public status: number) { super(message); this.name = 'ApiError'; }
}

function statusMessage(status: number): string {
  if (status === 429) return 'Too many requests. Please wait a minute and try again.';
  if (status >= 500) return 'The server is temporarily unavailable. Please try again shortly.';
  return `The request could not be completed (HTTP ${status}). Please try again.`;
}

export async function errorText(response: Response): Promise<string> {
  let data: any;
  try {
    data = await response.json();
  } catch {
    // Never log response bodies, URLs, or customer content.
    console.warn('API returned a non-JSON error response.', {status: response.status});
    return statusMessage(response.status);
  }
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) {
    const messages = data.detail.filter((item: any) => typeof item?.msg === 'string');
    if (messages.length) return messages.map((item: any) => item.msg.replace('Value error, ', '')).join(' ');
  }
  return statusMessage(response.status);
}

async function request(path: string, method: string, body?: any, signal?: AbortSignal): Promise<Response> {
  const form = body instanceof FormData;
  const headers: Record<string, string> = {'X-Workspace-Client': client, 'X-Triage-Request': '1'};
  if (!form) headers['Content-Type'] = 'application/json';
  const response = await fetch(BASE + path, {method, headers, signal, credentials: 'include',
    body: body ? (form ? body : JSON.stringify(body)) : undefined});
  if (!response.ok) {
    if (!path.startsWith('/auth/') && [401, 403].includes(response.status)) window.dispatchEvent(new Event('triage-auth-changed'));
    throw new ApiError(await errorText(response), response.status);
  }
  return response;
}

export async function api<T = any>(path: string, method = 'GET', body?: any, signal?: AbortSignal): Promise<T> {
  const response = await request(path, method, body, signal);
  return response.json();
}

function processEvent(block: string, progress: (stage: string) => void): Ticket | null {
  const type = block.match(/event: (.+)/)?.[1];
  const raw = block.match(/data: (.+)/)?.[1];
  if (!raw) return null;
  const data = JSON.parse(raw);
  if (type === 'progress') progress(data.stage);
  return type === 'result' ? data : null;
}

export async function draftStream(path: string, body: any, progress: (stage: string) => void, signal?: AbortSignal): Promise<Ticket> {
  const response = await request(path, 'POST', body, signal);
  if (!response.body) throw new Error('Draft streaming is unavailable. Please try again.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: Ticket | null = null;
  try {
    while (true) {
      const {value, done} = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, {stream: true});
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';
      for (const block of blocks) result = processEvent(block, progress) || result;
    }
    buffer += decoder.decode();
    if (buffer.trim()) result = processEvent(buffer, progress) || result;
  } finally {
    reader.releaseLock();
  }
  if (!result) throw new Error('The connection ended. Your ticket may be saved in the ticket log.');
  return result;
}